<template>
  <div>
    <loader :loading="loading" />
    <div class="row mb-3">
      <div class="col-12 col-md-6">
        <div class="card" v-if="importedPayment">
          <div class="card-head justify-between">
            <h2 class="h5" :class="{ 'text-success': importedPayment.status === 'assigned' }">{{ $t(importedPayment.status) }}</h2>
          </div>
          <div class="card-body overflow-auto">
            <div class="row row-cols-3">
              <div class="text-center">
                <h4 v-text="$t('importedpayment.amount')" />
                <h2 class="text-secondary">{{ importedPayment.transaction_amount | currency }}</h2>
              </div>
              <div class="text-center">
                <h4 v-text="$t('importedpayment.linked_amount')" />
                <h2 class="text-primary">{{ importedPayment.linked_amount | currency }}</h2>
              </div>
              <div class="text-center">
                <h4 v-text="$t('importedpayment.unlinked_amount')" />
                <h2 class="text-primary">{{ (importedPayment.transaction_amount - importedPayment.linked_amount) | currency }}</h2>
              </div>
            </div>
            <hr>
            <field type="column" label="importedpayment.account_iban" :value="importedPayment.account_iban" />
            <field type="column" label="importedpayment.account_bic" :value="importedPayment.account_bic" />
            <field type="column" label="importedpayment.acount_name" :value="importedPayment.account_name" />
            <field type="column" label="importedpayment.reference" :value="importedPayment.transaction_reference" />
            <field type="column" label="importedpayment.transaction_date" :value="importedPayment.transaction_date | date" />
            <field type="column" label="importedpayment.address" :value="importedPayment.address" />
            <field type="column" label="importedpayment.informational_message1" :value="importedPayment.informational_message1" />
            <field type="column" label="importedpayment.informational_message2" :value="importedPayment.informational_message2" />
            <field type="column" label="importedpayment.informational_message3" :value="importedPayment.informational_message3" />
            <div v-if="importedPayment.payment && importedPayment.payments.length">
              <hr>
              <div class='row'
                v-for="payment in importedPayment.payments"
                :key='payment.id'
              >
                <div class='col-9' v-text="payment.invoice_reference" />
                <div class='col-2 text-nowrap'>{{ payment.receivable_amount | currency }}</div>
                <a>
                  <div @click.prevent='doRevertPayment(payment.uuid)' class='col-1'>
                    <icon name='unlink'></icon>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-6">
        <div class="card" v-if="!loading || schema">
          <div class="card-head justify-between">
            <h2 class="h5">{{ $t('importedPayment.assign.form.title') }}</h2>
          </div>
          <div class="card-body">
            <form @submit.prevent="submit" class="form" v-if="schema" :ref="schema.title">
              <json-form :schema="schema" v-model="model" :errors="errors" :labels="false"/>
              <button class="btn btn-primary">
                <icon name="search" class="mr-1" />
                {{ $t('importedPayment.assign.form.submit') }}
              </button>
              <button class="btn btn-light ml-2" type="reset" @click="model = {}">{{ $t('importedPayment.assign.form.reset') }}</button>
            </form>
          </div>
        </div>
      </div>
    </div>
    <div class="card" v-if="table">
      <div class="card-head">
        <h2 class="h5">{{ $t('importedPayment.assign.suggestions.title') }}</h2>
      </div>
      <div class="card-body">
        <data-table v-bind="{ model, endpoint, data: table }" @action="doAction" @bulkAction="doBulkAction" :ref="`${model}List`" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { FormComponent } from '@/mixins/FormComponent';
import { AxiosResponse } from 'axios';
import { EmitterAction, Table } from '@/models';
import { parseEndpoint } from '@/router';
import Field from '@/components/global/Field.vue';
import ImportedPaymentAssign from '@/views/importedpayment/Assign/Form.vue';
import { EventBus } from '@/services/EventBus';
import api from '@/api';
import { ImportedPayment } from '@/models/ImportedPayment';

@Component({
  components: { Field },
})
export default class ImportedPaymentView extends mixins(FormComponent) {
  protected model = 'importedPaymentAssign';
  protected table: null | Table = null;
  protected importedPayment: null | ImportedPayment = null;

  private created() {
    this.setEndpoint();
    this.loadData();
    EventBus.$on('imported_payment.assign.reload', this.loadData);
  }

  private beforeDestroy() {
    EventBus.$off('imported_payment.assign.reload', this.loadData);
  }

  protected afterLoad(res: AxiosResponse) {
    if (res.data.suggestions) {
      this.table = res.data.suggestions.table;
    }
    if(res.data.importedPayment) {
      this.importedPayment = res.data.importedPayment;
    }
  }

  protected afterSubmit(success: boolean, response: AxiosResponse) {
    this.afterLoad(response);
  }

  private async doAction(action: EmitterAction) {
    if (action.emit === 'assign') {
      await this.$store.dispatch('bars/openRightBar', {
        component: ImportedPaymentAssign,
        title: this.$t(`imported_payment.${action.emit}`),
        properties: {
          importedPayment: {
            uuid: this.importedPayment?.uuid,
          },
          receivable: {
            uuid: action.id,
          }
        }
      });
    }
  }

  private async doRevertPayment(uuid: string) {
    let endpoint = `/imported_payment/payment/${uuid}/delete`
    await this.showConfirmation(this.$t('imported_payment.payment.delete.confirmation') as string)
        .then(() => {
          api.delete(endpoint)
            .then(() => this.loadData());
        });
  }

  private doBulkAction() {

  }

  private setEndpoint() {
    const { params, meta } = this.$route;
    this.endpoint = parseEndpoint(meta?.endpoint, params);
  }

  protected async showConfirmation(message: string) {
    return new Promise<any>((resolve) => {
      this.$store.dispatch('modal/open', {
        body: message,
        actions: [
          {
            label: this.$t('modal.record.delete.cancel') as string,
            class: 'btn-dark',
            action: async () => this.$store.dispatch('modal/close'),
          },
          {
            label: this.$t('modal.record.delete.confirm') as string,
            class: 'btn-primary',
            action: async () => {
              this.$store.dispatch('modal/close').then(resolve);
            },
          },
        ],
      });
    });
  }
}
</script>
